import React, { useState } from 'react';
import { toast } from '../../Common/Toast';
import './MilitaryAssessmentSetupModal.css';

const MilitaryAssessmentSetupModal = ({ 
  show, 
  onClose, 
  onStart, 
  teamName, 
  selectedMember,
  selectedMembers = [],
  weekNumber = 1 
}) => {
  const [assessmentData, setAssessmentData] = useState({
    mode: 'video',
    capture: ['wearables', 'camera', 'voice'],
    language: 'english',
    week_number: weekNumber
  });
  const [isStarting, setIsStarting] = useState(false);

  const candidates = selectedMembers.length > 0 ? selectedMembers : (selectedMember ? [selectedMember] : []);
  const isMultiple = candidates.length > 1;

  const assessmentModes = [
    { value: 'in_person', label: '👥 In Person' },
    { value: 'video', label: '📹 Video Meeting' },
  ];

  const assessmentLanguage = [
    { value: 'english', label: 'English' },
    { value: 'hindi', label: 'Hindi' },
  ];

  const captureModes = [
    { value: 'wearables', label: '⌚ Wearables' },
    { value: 'voice', label: '🎤 Voice' },
    { value: 'camera', label: '📸 Camera' }
  ];

  const handleModeChange = (e) => {
    setAssessmentData(prev => ({ 
      ...prev, 
      mode: e.target.value, 
      capture: e.target.value === 'video' ? ['wearables', 'camera', 'voice'] : ['wearables'] 
    }));
  };

  const handleLanguageChange = (e) => {
    setAssessmentData(prev => ({ 
      ...prev, 
      language: e.target.value, 
    }));
  };

  const handleCaptureChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    
    setAssessmentData(prev => ({
      ...prev,
      capture: isChecked 
        ? [...prev.capture, value]
        : prev.capture.filter(item => item !== value)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isStarting) return;
    
    setIsStarting(true);
    try {
      await onStart({
        ...assessmentData,
        user_id: selectedMember?.user_id,
        candidate_name: selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : null,
        candidates: candidates.map(c => ({
          user_id: c.user_id,
          candidate_name: `${c.first_name} ${c.last_name}`
        })),
        candidate_count: candidates.length,
        week_number: weekNumber,
        is_batch_assessment: isMultiple
      });
      onClose();
    } catch (error) {
      console.error('Error in military assessment setup:', error);
      toast.error(error.message || 'Failed to start assessment session');
    } finally {
      setIsStarting(false);
    }
  };

  const getWeekDescription = (week) => {
    const descriptions = {
      1: 'Initial Assessment - Basic aptitude and personality evaluation',
      2: 'Technical Skills - Problem-solving and technical knowledge',
      3: 'Leadership & Teamwork - Collaboration and leadership potential',
      4: 'Stress Management - Performance under pressure',
      5: 'Strategic Thinking - Planning and decision-making',
      6: 'Final Evaluation - Comprehensive assessment and role matching'
    };
    return descriptions[week] || 'Weekly assessment session';
  };

  if (!show) return null;

  return (
    <div className="teams-modal-overlay">
      <div className="teams-modal military-setup-modal">
        <div className="teams-modal-header">
          <h2>
            Week {weekNumber} Assessment
            {isMultiple ? (
              <span className="candidate-batch-info">
                {candidates.length} Candidates
              </span>
            ) : selectedMember && (
              <span className="candidate-name">
                {selectedMember.first_name} {selectedMember.last_name}
              </span>
            )}
          </h2>
          <button className="teams-modal-close" onClick={onClose} type="button" disabled={isStarting}>
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="teams-modal-form">
          <div className="form-group">
            <div className="info-banner mb-3 mt-1">
              <span className="me-1">ℹ️</span>
              <span>Week {weekNumber} of 6: {getWeekDescription(weekNumber)}</span>
            </div>
            
            <div className="info-banner-secondary mb-3">
              <span className="me-1">⏱️</span>
              <span>30 questions with 2 minutes per question. Timer starts when question appears.</span>
            </div>

            {isMultiple && (
              <div className="candidates-summary mb-3">
                <div className="summary-header">
                  <span>👥 Selected Candidates ({candidates.length})</span>
                </div>
                <div className="candidates-list-compact">
                  {candidates.slice(0, 3).map((c, i) => (
                    <span key={c.user_id} className="candidate-tag">
                      {c.first_name} {c.last_name}
                    </span>
                  ))}
                  {candidates.length > 3 && (
                    <span className="candidate-tag more">+{candidates.length - 3} more</span>
                  )}
                </div>
              </div>
            )}
            
            <label>Assessment Mode *</label>
            <select 
              value={assessmentData.mode} 
              onChange={handleModeChange} 
              className="team-type-select" 
              required 
              disabled={isStarting}
            >
              {assessmentModes.map(mode => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>

            <label className='mt-2'>Language *</label>
            <select 
              value={assessmentData.language} 
              onChange={handleLanguageChange} 
              className="team-type-select" 
              required 
              disabled={isStarting}
            >
              {assessmentLanguage.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Capture Mode *</label>
            <div className="checkbox-group">
              {captureModes.map(mode => (
                <label key={mode.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    value={mode.value}
                    checked={assessmentData.capture.includes(mode.value)}
                    onChange={handleCaptureChange}
                    disabled={isStarting}
                  />
                  <span className="ms-2 checkbox-text">{mode.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="teams-modal-actions">
            <button type="button" className="masm-cancel-btn" onClick={onClose} disabled={isStarting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isStarting}>
              {`Start Assessment`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MilitaryAssessmentSetupModal;